import { Button, Fieldset, Heading, Paragraph } from '@digdir/designsystemet-react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, XMarkIcon } from '@navikt/aksel-icons';

import styled from '../../../../../styled';
import { ByggType } from '../../../../../components/registering/utils/registerEnums';
import { FormErrors } from '../../../../../components/form/formErrors';
import { ButtonWrapper } from '../../solceller/form/components/styledComponents';

import { OmBoligenFormData } from './types';
import { ByggtypeSelect } from './components/byggtypeSelect';
import { VertikalPlasseringLeilighetRadio } from './components/vertikalPlasseringLeilighetRadio';
import { ByggearInput } from './components/byggearInput';
import { AntallEtasjerInput } from './components/antallEtasjerInput';
import { KonstruksjonsmaterialeRadio } from './components/konstruksjonsmaterialeRadio';
import { OppvarmetBruksarealInput } from './components/oppvarmetBruksarealInput';
import { BruksarealInput } from './components/bruksarealInput';
import { FormCard } from './components/styledComponents';
import { HorisontalPlasseringLeilighetRadio } from './components/horisontalPlasseringLeilighetRadio';
import { HarKjellerRadio } from './components/harKjellerRadio';
import { HorisontalPlasseringRekkehusRadio } from './components/horisontalPlasseringRekkehusRadio';
import { VertikalPlasseringRadio } from './components/vertikalPlasseringRadio';
import { TotalOppvarmetBraBygningInput } from './components/totalOppvarmetBraBygningInput';

interface Props {
  defaultValues?: OmBoligenFormData;
  onSubmit: (formData: OmBoligenFormData) => void;
  isLoading?: boolean;
  buildingId: string;
}

export const FormContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--ds-size-7);
`;

export const OmBoligenForm: FC<Props> = ({ defaultValues, onSubmit, isLoading, buildingId }) => {
  const methods = useForm<OmBoligenFormData>({ defaultValues });

  const { t } = useTranslation();

  const byggType = methods.watch('bygginfo.byggtype');

  const isRekkehus = byggType === ByggType.Rekkehus;
  const isLeilighet = byggType === ByggType.Leilighet;
  const isFiremannsbolig = byggType === ByggType.Firemannsbolig;
  const isHorisontaldeltTomannsbolig = byggType === ByggType.TomannsboligHorisontalDelt;
  const isEnebolig = byggType === ByggType.Enebolig;
  const isEneboligMedUtleie = byggType === ByggType.EneboligMedUtleiedel;

  const showTotaltOppvarmetBraBygning = !!byggType && !(isEnebolig || isEneboligMedUtleie || isLeilighet);

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
        <FormContentWrapper>
          <FormCard>
            <Fieldset disabled={isLoading}>
              <Fieldset.Legend>
                <Heading level={2} data-size="md">
                  {t('register.general.aboutBuilding.heading')}
                </Heading>
              </Fieldset.Legend>
              <Paragraph>{t('register.general.aboutBuilding.description')}</Paragraph>
              <ByggtypeSelect />

              {isLeilighet && <VertikalPlasseringLeilighetRadio />}
              {isLeilighet && <HorisontalPlasseringLeilighetRadio />}
              {isRekkehus && <HorisontalPlasseringRekkehusRadio />}
              {(isFiremannsbolig || isHorisontaldeltTomannsbolig) && <VertikalPlasseringRadio />}

              <ByggearInput />
              <HarKjellerRadio />
              <AntallEtasjerInput />
              <BruksarealInput />
              <OppvarmetBruksarealInput />
              {showTotaltOppvarmetBraBygning && <TotalOppvarmetBraBygningInput />}
              <KonstruksjonsmaterialeRadio />
            </Fieldset>
          </FormCard>

          <FormErrors errors={methods.formState.errors} />

          <ButtonWrapper>
            <Button disabled={isLoading} asChild variant="tertiary" data-color="danger">
              <Link to={`/bygg/${buildingId}`}>
                <XMarkIcon /> {t('form.common.cancel')}
              </Link>
            </Button>
            <Button loading={isLoading} type="submit">
              {t('form.common.next')} <ChevronRightIcon />
            </Button>
          </ButtonWrapper>
        </FormContentWrapper>
      </form>
    </FormProvider>
  );
};
